// Single source of truth for a task's in-app link. Every place that navigates to /
// links to a task MUST go through here instead of hand-concatenating the URL, so no caller
// can accidentally link an un-persisted task (SCD-948).
//
// Returns null when the task can't be opened yet:
// - `pending` — an optimistic/just-created task whose Firestore doc (and real `number`)
//   hasn't arrived. Its placeholder `number` is 0, which used to build `/task/PREFIX-0`,
//   a URL that resolves to no task and rendered a "Not found" over the project.
// - a non-positive / non-integer / missing `number` (0 is the optimistic placeholder).
// - a missing workspace slug or prefix.

interface TaskPathParams {
    workspaceUrlId: string | undefined | null
    prefix: string | undefined | null
    number: number | undefined | null
    // True for an optimistic task that isn't saved yet — never linkable.
    pending?: boolean
}

export function taskPath({ workspaceUrlId, prefix, number, pending }: TaskPathParams): string | null {
    if (pending) return null
    if (!workspaceUrlId || !prefix) return null
    if (typeof number !== 'number' || !Number.isInteger(number) || number <= 0) return null
    return `/${workspaceUrlId}/task/${prefix}-${number}`
}

// Absolute URL variant (e.g. the copy-link action). Same guards; null when not linkable.
export function taskUrl(origin: string, params: TaskPathParams): string | null {
    const path = taskPath(params)
    return path ? `${origin}${path}` : null
}
